import React, { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Pencil, Plus, Trash2 } from "lucide-react";
import { Button, Input } from "../../atoms";
import { FormField } from "../FormField";
import { Modal } from "../Modal";
import { SettingsSectionCard } from "@/components/layout/SettingsSectionCard";
import { teamAPI } from "@/services/api/team.service";
import type {
  CreateTeamMemberData,
  TeamCompensationType,
  TeamEmploymentType,
  TeamMember,
} from "@/types/startup.types";
import { toDateInputValue } from "@/utils/date";

const EMPTY_FORM: CreateTeamMemberData = {
  name: "",
  title: "",
  employmentType: "full_time",
  compensationType: "salary",
  startDate: new Date().toISOString().slice(0, 10),
  autoSyncExpense: true,
  showInInvestorView: true,
};

interface TeamRosterPanelProps {
  startupId: string;
}

export const TeamRosterPanel: React.FC<TeamRosterPanelProps> = ({ startupId }) => {
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [editing, setEditing] = useState<TeamMember | null>(null);
  const [form, setForm] = useState<CreateTeamMemberData>(EMPTY_FORM);

  const { data: members = [], isLoading } = useQuery({
    queryKey: ["teamMembers", startupId],
    queryFn: () => teamAPI.listTeamMembers(startupId),
    enabled: Boolean(startupId),
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (editing) {
        return teamAPI.updateTeamMember(startupId, editing.id, form);
      }
      return teamAPI.createTeamMember(startupId, form);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers", startupId] });
      setIsOpen(false);
      setEditing(null);
      setForm(EMPTY_FORM);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (memberId: string) => teamAPI.deleteTeamMember(startupId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["teamMembers", startupId] });
    },
  });

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setIsOpen(true);
  };

  const openEdit = (member: TeamMember) => {
    setEditing(member);
    setForm({
      name: member.name,
      title: member.title,
      department: member.department,
      employmentType: member.employmentType,
      startDate: member.startDate.slice(0, 10),
      endDate: member.endDate?.slice(0, 10),
      compensationType: member.compensationType,
      monthlyCashCost: member.monthlyCashCost,
      annualSalary: member.annualSalary,
      equityPercent: member.equityPercent,
      equityNotes: member.equityNotes,
      autoSyncExpense: member.autoSyncExpense,
      showInInvestorView: member.showInInvestorView,
      notes: member.notes,
      sortOrder: member.sortOrder,
    });
    setIsOpen(true);
  };

  return (
    <SettingsSectionCard
      title="Team roster"
      description="People on your team. Names, titles, and bios appear in the investor business plan. Salary compensation can sync to personnel expenses automatically."
    >
      <div className="mb-4 flex justify-end">
        <Button type="button" variant="primary" size="sm" onClick={openCreate}>
          <Plus className="mr-1 h-4 w-4" />
          Add team member
        </Button>
      </div>

      {isLoading ? (
        <div className="text-sm text-white/55">Loading team...</div>
      ) : members.length === 0 ? (
        <div className="rounded-[14px] border border-dashed border-white/15 p-6 text-center text-sm text-white/55">
          No team members yet. Add your founding team and key hires.
        </div>
      ) : (
        <div className="space-y-3">
          {members.map((member) => (
            <div
              key={member.id}
              className="flex items-start justify-between gap-3 rounded-[14px] border border-white/10 bg-surface/40 p-4"
            >
              <div>
                <div className="font-semibold text-white">
                  {member.name}
                  <span className="ml-2 text-sm font-normal text-white/55">
                    {member.title}
                  </span>
                </div>
                <div className="mt-1 text-xs text-white/50">
                  {member.employmentType.replace("_", " ")} · {member.compensationType.replace("_", " ")}
                  {member.department ? ` · ${member.department}` : ""}
                  {member.monthlyCashCost
                    ? ` · $${member.monthlyCashCost.toLocaleString()}/mo`
                    : ""}
                  {member.equityPercent != null ? ` · ${member.equityPercent}% equity` : ""}
                </div>
                {member.notes ? (
                  <p className="mt-2 text-sm text-white/65 line-clamp-3">{member.notes}</p>
                ) : null}
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => openEdit(member)}
                  className="rounded-lg p-2 text-white/55 hover:bg-white/10 hover:text-white"
                >
                  <Pencil className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm(`Remove ${member.name} from the roster?`)) {
                      deleteMutation.mutate(member.id);
                    }
                  }}
                  className="rounded-lg p-2 text-white/55 hover:bg-danger/10 hover:text-danger"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        title={editing ? "Edit team member" : "Add team member"}
      >
        <div className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Name">
              <Input
                value={form.name}
                onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
              />
            </FormField>
            <FormField label="Title / role">
              <Input
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
              />
            </FormField>
          </div>
          <FormField label="Department (optional)">
            <Input
              value={form.department ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, department: e.target.value || undefined }))
              }
              placeholder="e.g. Engineering, Operations"
            />
          </FormField>
          <FormField label="Bio / description">
            <textarea
              className="sh-select min-h-[96px]"
              value={form.notes ?? ""}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, notes: e.target.value || undefined }))
              }
              placeholder="Relevant experience, domain expertise, and responsibilities for the business plan…"
            />
          </FormField>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Employment type">
              <select
                className="sh-select"
                value={form.employmentType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    employmentType: e.target.value as TeamEmploymentType,
                  }))
                }
              >
                <option value="founder">Founder</option>
                <option value="full_time">Full-time</option>
                <option value="part_time">Part-time</option>
                <option value="contractor">Contractor</option>
                <option value="advisor">Advisor</option>
              </select>
            </FormField>
            <FormField label="Compensation">
              <select
                className="sh-select"
                value={form.compensationType}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    compensationType: e.target.value as TeamCompensationType,
                  }))
                }
              >
                <option value="salary">Salary</option>
                <option value="salary_and_equity">Salary + equity</option>
                <option value="equity_only">Equity only</option>
                <option value="contractor">Contractor</option>
                <option value="unpaid">Unpaid</option>
              </select>
            </FormField>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Start date">
              <Input
                type="date"
                value={toDateInputValue(form.startDate)}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, startDate: e.target.value }))
                }
              />
            </FormField>
            <FormField label="Monthly cash cost ($)">
              <Input
                type="number"
                value={form.monthlyCashCost?.toString() ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    monthlyCashCost: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </FormField>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField label="Equity % (optional)">
              <Input
                type="number"
                step="0.01"
                value={form.equityPercent?.toString() ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    equityPercent: e.target.value ? Number(e.target.value) : undefined,
                  }))
                }
              />
            </FormField>
            <FormField label="Equity notes (optional)">
              <Input
                value={form.equityNotes ?? ""}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, equityNotes: e.target.value }))
                }
              />
            </FormField>
          </div>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input
              type="checkbox"
              checked={form.autoSyncExpense !== false}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, autoSyncExpense: e.target.checked }))
              }
            />
            Sync salary to personnel expense
          </label>
          <label className="flex items-center gap-2 text-sm text-white/70">
            <input
              type="checkbox"
              checked={form.showInInvestorView !== false}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, showInInvestorView: e.target.checked }))
              }
            />
            Visible in investor view
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="secondary" size="md" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button
              type="button"
              variant="primary"
              size="md"
              disabled={saveMutation.isPending || !form.name || !form.title}
              onClick={() => saveMutation.mutate()}
            >
              {saveMutation.isPending ? "Saving..." : "Save"}
            </Button>
          </div>
        </div>
      </Modal>
    </SettingsSectionCard>
  );
};
